import React, { useState, useEffect, useRef } from "react";
import { Icon, Popup } from "semantic-ui-react";
import PropTypes from "prop-types";
import styles from "./styles.module.css";
import { processStages, renderEmptyState, renderHistoryList, handleGlobalToggle } from "./helpers.jsx";

const MenuHistory = ({
	stages,
	forceExpandAll = false,
	expandedMenus: expandedMenusProp,
	setExpandedMenus: setExpandedMenusProp,
	expandedOptions: expandedOptionsProp,
	setExpandedOptions: setExpandedOptionsProp,
}) => {
	const [historyItems, setHistoryItems] = useState([]);
	const [internalExpandedMenus, internalSetExpandedMenus] = useState({});
	const [internalExpandedOptions, internalSetExpandedOptions] = useState({});
	const expandedMenus = expandedMenusProp ?? internalExpandedMenus;
	const setExpandedMenus = setExpandedMenusProp ?? internalSetExpandedMenus;
	const expandedOptions = expandedOptionsProp ?? internalExpandedOptions;
	const setExpandedOptions = setExpandedOptionsProp ?? internalSetExpandedOptions;

	const [legendOpen, setLegendOpen] = useState(false);
	const expandRef = useRef(null);

	useEffect(() => {
		const sortedStages = [...stages].sort((a, b) => {
			return (a.order ?? 0) - (b.order ?? 0);
		});
		setHistoryItems(processStages(sortedStages));
	}, [stages]);

	useEffect(() => {
		if (forceExpandAll && historyItems.length > 0) {
			const allMenus = {};
			const allOptions = {};
			historyItems.forEach((item) => {
				if (item.type === "stage") {
					allMenus[item.stage.id] = true;
				}
				if (item.type === "option") {
					allOptions[`option-${item.stageIndex !== undefined ? item.stageIndex : ""}-${item.option?.id ?? ""}`] = true;
				}
			});
			setExpandedMenus(allMenus);
			setExpandedOptions(allOptions);
		}
	}, [forceExpandAll, historyItems]);

	const allMenusOpen =
		stages.length > 0 &&
		stages.every((stage) => {
			return expandedMenus[stage.id];
		});
	const handleClickToggle = () => {
		handleGlobalToggle({
			expandedAll: allMenusOpen,
			setExpandedAll: () => {},
			stages,
			setExpandedMenus,
			setExpandedOptions,
		});
	};

	const sortedStages = [...stages].sort((a, b) => {
		return (a.order ?? 0) - (b.order ?? 0);
	});

	return (
		<div className={styles.container}>
			<div className={styles.subContainerHeader}>
				<h2 className={styles.subContainerTitle}>
					<Icon name="history" className={styles.icon} /> Menú
					<Icon
						name="info circle"
						className={styles.legendInfoIcon}
						onClick={() => {
							return setLegendOpen((prev) => {
								return !prev;
							});
						}}
					/>
					<Popup
						context={expandRef}
						open={legendOpen}
						onClose={() => {
							return setLegendOpen(false);
						}}
						position="bottom right"
						keepInViewPort={false}
						content={
							<div className={styles.legendPopupContent}>
								<strong className={styles.legendTitle}>Leyenda de Iconos</strong>
								<div className={styles.legendContainer}>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="list" color="blue" className={styles.legendIcon} />
										</div>
										<span className={styles.legendText}>Menú / Submenú</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="circle outline" className={`${styles.legendIcon} ${styles.colorGreen}`} />
										</div>
										<span className={styles.legendText}>Opción Final</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="angle right" className={`${styles.legendIcon} ${styles.colorGreen}`} />
										</div>
										<span className={styles.legendText}>Opción con Submenú</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="reply" color="blue" className={styles.legendIcon} />
										</div>
										<span className={styles.legendText}>Volver Atrás</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="home" color="blue" className={styles.legendIcon} />
										</div>
										<span className={styles.legendText}>Volver al Inicio</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="power off" color="red" className={styles.legendIcon} />
										</div>
										<span className={styles.legendText}>Cerrar Sesión</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="linkify" className={`${styles.legendIcon} ${styles.colorPurple}`} />
										</div>
										<span className={styles.legendText}>Botón URL</span>
									</div>
									<div className={styles.legendRow}>
										<div className={styles.legendIconWrapper}>
											<Icon name="phone" className={`${styles.legendIcon} ${styles.colorPurple}`} />
										</div>
										<span className={styles.legendText}>Botón Teléfono</span>
									</div>
									<div className={styles.legendRowLast}>
										<div className={styles.legendIconWrapper}>
											<Icon name="file alternate outline" className={`${styles.legendIcon} ${styles.colorPurple}`} />
										</div>
										<span className={styles.legendText}>Botón Flow</span>
									</div>
								</div>
							</div>
						}
					/>
				</h2>
				<div className={styles.expandAllContainer} ref={expandRef}>
					<span className={styles.expandText}>{allMenusOpen ? "Colapsar Todas" : "Expandir Todas"}</span>
					<Icon name={allMenusOpen ? "angle up" : "angle down"} onClick={handleClickToggle} className={styles.expandIcon} />
				</div>
			</div>
			<div className={styles.historyContainer}>
				<div className={styles.historyHeader}>
					<Icon name="history" className={styles.historyIcon} />
					<span className={styles.historyTitle}>Menú</span>
				</div>
				<div className={styles.historyContent}>
					{historyItems.length === 0
						? renderEmptyState(styles)
						: renderHistoryList({
								historyItems,
								stages: sortedStages,
								expandedMenus,
								expandedOptions,
								setExpandedMenus,
								setExpandedOptions,
								styles,
						  })}
				</div>
			</div>
		</div>
	);
};

MenuHistory.propTypes = {
	stages: PropTypes.array.isRequired,
	forceExpandAll: PropTypes.bool,
	expandedMenus: PropTypes.object,
	setExpandedMenus: PropTypes.func,
	expandedOptions: PropTypes.object,
	setExpandedOptions: PropTypes.func,
};

export default MenuHistory;
